import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import Popup from "../../components/Popup";
import {
    checkCataloguePreview,
    createScheme,
    deleteScheme,
    getMySchemes,
    getSchemeLookups,
} from "../../redux/schemeRelated/schemeHandle";
import { getCataloguePreviewSuccess, underSchemeControl } from "../../redux/schemeRelated/schemeSlice";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";

const TERM_OPTIONS = ["Term 1", "Term 2", "Term 3"];
const PERIODS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

const STATUS_CHIP = {
    Draft: "default",
    Submitted: "info",
    Approved: "success",
    Returned: "warning",
};

function schemeLabel(scheme) {
    const subjectName = scheme?.subject?.subName || "Subject";
    const className = scheme?.sclass?.sclassName || scheme?.class?.sclassName || "Class";
    return `${subjectName} — ${className} · ${scheme?.term || ""} ${scheme?.academicYear || ""}`.trim();
}

function schemeStats(scheme) {
    const entries = Array.isArray(scheme?.entries) ? scheme.entries : [];
    const taught = entries.filter((e) => e.isTaught).length;
    return {
        blockCount: entries.length,
        taughtCount: taught,
        periodsPerWeek: scheme?.periodsPerWeek ?? 5,
    };
}

function extractForm(className) {
    if (!className) return "";
    const m = className.match(/^(Form[1-4]|Std[1-8]|Year[1-5])/i);
    return m
        ? m[1].replace(/^form/i, "Form").replace(/^std/i, "Std").replace(/^year/i, "Year")
        : "";
}

function formatTopicPreview(topicPreview) {
    const text = (topicPreview || []).map((t) => t.topic).filter(Boolean).join(" · ");
    if (text.length <= 80) return text;
    return `${text.slice(0, 80)}...`;
}

const SchemeOfWork = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { schemesList, loading, statestatus, response, error, cataloguePreview } = useSelector(
        (s) => s.scheme
    );

    const teacherId = currentUser?._id;
    const schoolId = currentUser?.school?._id || currentUser?.schoolId;
    const { activeSubjectId: subjectId, activeClassId: classId, subjectName, className } = useTeacherTeaching();
    const detectedForm = useMemo(() => extractForm(className), [className]);

    const [createOpen, setCreateOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [shouldLoadTemplate, setShouldLoadTemplate] = useState(false);
    const [pendingNavigate, setPendingNavigate] = useState(null);

    const createPendingRef = useRef(false);

    const [term, setTerm] = useState("Term 1");
    const [academicYear, setAcademicYear] = useState(String(new Date().getFullYear()));
    const [periodsPerWeek, setPeriodsPerWeek] = useState(5);

    const schemes = useMemo(
        () => (Array.isArray(schemesList) ? schemesList : []),
        [schemesList]
    );

    const reload = useCallback(() => {
        if (teacherId) dispatch(getMySchemes(teacherId));
    }, [dispatch, teacherId]);

    useEffect(() => {
        if (teacherId) dispatch(getMySchemes(teacherId));
        if (subjectName) {
            dispatch(getSchemeLookups(subjectName, detectedForm, term));
        }
    }, [dispatch, teacherId, subjectName, detectedForm, term]);

    useEffect(() => {
        if (statestatus === "added") {
            if (createPendingRef.current) {
                createPendingRef.current = false;
                reload();
                setCreateOpen(false);
                setPendingNavigate({ loadTemplate: shouldLoadTemplate });
                dispatch(underSchemeControl());
                return;
            }
            setMessage("Completed successfully!");
            setShowPopup(true);
            reload();
            setCreateOpen(false);
            dispatch(underSchemeControl());
        }
    }, [statestatus, dispatch, reload, shouldLoadTemplate]);

    useEffect(() => {
        if (!pendingNavigate || loading) return;
        const newSchemeId = schemes[0]?._id || schemes[0]?.id;
        if (!newSchemeId) return;
        if (pendingNavigate.loadTemplate) {
            navigate(`/Teacher/schemes/${newSchemeId}?loadTemplate=1`);
        } else {
            setMessage("Completed successfully!");
            setShowPopup(true);
        }
        setPendingNavigate(null);
    }, [pendingNavigate, schemes, loading, navigate]);

    useEffect(() => {
        if (!createOpen || !subjectName || !detectedForm || !term) return;
        dispatch(checkCataloguePreview(subjectName, detectedForm, term));
    }, [createOpen, subjectName, detectedForm, term, dispatch]);

    useEffect(() => {
        setShouldLoadTemplate(Boolean(cataloguePreview?.hasTemplate));
    }, [cataloguePreview]);

    useEffect(() => {
        if (statestatus === "failed" || (response && statestatus !== "added")) {
            setMessage(typeof response === "string" ? response : "Request failed");
            setShowPopup(true);
            dispatch(underSchemeControl());
        }
    }, [response, statestatus, dispatch]);

    useEffect(() => {
        if (error && statestatus !== "added") {
            const msg =
                typeof error === "string"
                    ? error
                    : error?.message || "Network error";
            setMessage(msg);
            setShowPopup(true);
            dispatch(underSchemeControl());
        }
    }, [error, statestatus, dispatch]);

    const openCreateDialog = () => {
        setTerm("Term 1");
        setAcademicYear(String(new Date().getFullYear()));
        setPeriodsPerWeek(5);
        setShouldLoadTemplate(false);
        setCreateOpen(true);
    };

    const closeCreateDialog = () => {
        setCreateOpen(false);
        dispatch(getCataloguePreviewSuccess(null));
    };

    const handleCreate = () => {
        if (!teacherId || !subjectId || !classId || !schoolId) {
            setMessage("Missing teacher, subject, class or school information.");
            setShowPopup(true);
            return;
        }
        if (!term || !academicYear) {
            setMessage("Term and academic year are required.");
            setShowPopup(true);
            return;
        }

        createPendingRef.current = true;
        dispatch(
            createScheme({
                teacherId,
                subjectId,
                classId,
                schoolId,
                term,
                academicYear: String(academicYear).trim(),
                periodsPerWeek: Number(periodsPerWeek),
            })
        );
    };

    const handleDelete = (scheme) => {
        const id = scheme._id || scheme.id;
        if (!id) return;
        if (!window.confirm("Delete this draft scheme? This cannot be undone.")) return;
        dispatch(deleteScheme(id));
    };

    const showEmpty = !loading && schemes.length === 0;

    return (
        <>
            <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "flex-start" }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Schemes & Records of Work
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Ministry of Education · MIE/MANEB aligned · {subjectName}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreateDialog}
                        sx={{ alignSelf: { xs: "stretch", sm: "center" }, fontWeight: 700 }}
                    >
                        New Scheme
                    </Button>
                </Stack>

                {loading && schemes.length === 0 ? (
                    <Stack spacing={1.5}>
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} variant="rounded" height={80} />
                        ))}
                    </Stack>
                ) : showEmpty ? (
                    <Paper
                        variant="outlined"
                        sx={{
                            py: 6,
                            px: 2,
                            textAlign: "center",
                            borderRadius: 2,
                        }}
                    >
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                            No schemes yet for this subject. Create your first scheme for the term.
                        </Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                            Create Scheme
                        </Button>
                    </Paper>
                ) : (
                    schemes.map((scheme) => {
                        const id = scheme._id || scheme.id;
                        const status = scheme.status || "Draft";
                        const stats = schemeStats(scheme);

                        return (
                            <Paper
                                key={id}
                                variant="outlined"
                                sx={{ p: 2, mb: 1.5, borderRadius: 2 }}
                            >
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                    spacing={1}
                                >
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {schemeLabel(scheme)}
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Chip
                                            label={status}
                                            size="small"
                                            color={STATUS_CHIP[status] || "default"}
                                        />
                                        <Tooltip title="Open scheme">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => navigate(`/Teacher/schemes/${id}`)}
                                            >
                                                <MenuBookOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {status === "Draft" ? (
                                            <Tooltip title="Delete draft">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(scheme)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                    </Stack>
                                </Stack>

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                    sx={{ mt: 1 }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {stats.blockCount} week blocks · {stats.taughtCount} taught ·
                                        Periods/week: {stats.periodsPerWeek}
                                    </Typography>
                                    {status === "Draft" ? (
                                        <Button
                                            size="small"
                                            onClick={() => navigate(`/Teacher/schemes/${id}`)}
                                            sx={{ textTransform: "none", fontWeight: 600 }}
                                        >
                                            Edit & Add Entries →
                                        </Button>
                                    ) : null}
                                </Stack>
                            </Paper>
                        );
                    })
                )}
            </Container>

            <Dialog open={createOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Create Scheme of Work</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Northern Education Division · Ministry of Education format
                    </Typography>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <TextField
                            fullWidth
                            label="Subject"
                            value={subjectName}
                            InputProps={{ readOnly: true }}
                        />
                        <TextField
                            fullWidth
                            label="Class"
                            value={className}
                            InputProps={{ readOnly: true }}
                        />
                        <FormControl fullWidth required>
                            <InputLabel id="scheme-term-label">Term</InputLabel>
                            <Select
                                labelId="scheme-term-label"
                                label="Term"
                                value={term}
                                onChange={(e) => setTerm(e.target.value)}
                            >
                                {TERM_OPTIONS.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            required
                            label="Academic Year"
                            value={academicYear}
                            onChange={(e) => setAcademicYear(e.target.value)}
                            helperText="e.g. 2025"
                        />
                        {cataloguePreview !== null ? (
                            cataloguePreview.hasTemplate ? (
                                <Paper
                                    variant="outlined"
                                    sx={{ p: 1.5, bgcolor: "action.hover" }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <AutoAwesomeIcon fontSize="small" color="primary" />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                Template available — {cataloguePreview.entryCount} week blocks ready
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Topics: {formatTopicPreview(cataloguePreview.topicPreview)}
                                            </Typography>
                                        </Box>
                                        <Chip label="Will auto-load" color="primary" size="small" />
                                    </Stack>
                                </Paper>
                            ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                    No template for this combination — you will enter week blocks manually
                                </Typography>
                            )
                        ) : null}
                        <FormControl fullWidth>
                            <InputLabel id="scheme-periods-label">Periods Per Week</InputLabel>
                            <Select
                                labelId="scheme-periods-label"
                                label="Periods Per Week"
                                value={periodsPerWeek}
                                onChange={(e) => setPeriodsPerWeek(Number(e.target.value))}
                            >
                                {PERIODS_OPTIONS.map((n) => (
                                    <MenuItem key={n} value={n}>
                                        {n}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, ml: 1.5 }}>
                                Standard is 5 for secondary Math/Science
                            </Typography>
                        </FormControl>
                        <Paper variant="outlined" sx={{ bgcolor: "action.hover", p: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                After creating, you will add weekly entries with topics, activities and
                                resources.
                            </Typography>
                        </Paper>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeCreateDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={loading}>
                        Create Scheme
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </>
    );
};

export default SchemeOfWork;
